/**
 * KanbanTools — popover-style toolbar relocated from the deprecated Products
 * page. Exposes Import/Export/Restore/Documents/Add-contact actions on the
 * leads collection (CSV in/out), wired to the existing /api/leads endpoints.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench, Upload, Download, RotateCcw, FileText, UserPlus,
  X, Loader2, Building2, Phone, Mail, Globe, Sparkles, Copy, Check,
  FileSpreadsheet, ExternalLink, Send,
} from "lucide-react";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "./TrialLock";
import ClaireHelpButton from "./ClaireHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KanbanTools({ isPt = false, onChanged }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("import");

  return (
    <>
      <button
        type="button"
        data-testid="kanban-tools-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#3B82F6] bg-[#EFF6FF] text-[12.5px] font-semibold text-[#3B82F6] hover:bg-[#DBEAFE] transition-colors"
      >
        <Wrench className="w-3.5 h-3.5" />
        {isPt ? "Ferramentas" : "Tools"}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <div
            data-testid="kanban-tools-modal"
            onClick={(e) => e.stopPropagation()}
            className="al-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <header className="flex items-center justify-between px-5 py-3 border-b border-[var(--al-border)]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#3B82F6] flex items-center justify-center">
                  <Wrench className="w-4.5 h-4.5" />
                </div>
                <div>
                  <h2 className="text-[14.5px] font-bold">{isPt ? "Ferramentas do CRM" : "CRM Tools"}</h2>
                  <p className="text-[11.5px] text-[var(--al-text-muted)]">{isPt ? "Importar, exportar, restaurar e documentar leads." : "Import, export, restore and document leads."}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ClaireHelpButton
                  testid="kanban-tools"
                  label={isPt ? "Pedir ajuda da Claire" : "Ask Claire"}
                  question={
                    isPt
                      ? "Como uso as Ferramentas do CRM (importar, exportar e disparar e-mail em massa)?"
                      : "How do I use the CRM Tools (import, export, mass email)?"
                  }
                />
                <button type="button" onClick={() => setOpen(false)}
                  data-testid="kanban-tools-close"
                  className="text-[var(--al-text-muted)] hover:text-rose-500 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </header>

            <div className="flex border-b border-[var(--al-border)] overflow-x-auto">
              {[
                { id: "import", icon: Upload, en: "Import data", pt: "Importar dados" },
                { id: "export", icon: Download, en: "Export data", pt: "Exportar dados" },
                { id: "blast", icon: Send, en: "Email Blast", pt: "Disparo de E-mail" },
                { id: "restore", icon: RotateCcw, en: "Restore data", pt: "Restaurar dados" },
                { id: "documents", icon: FileText, en: "Documents", pt: "Documentos" },
                { id: "add", icon: UserPlus, en: "Add contact", pt: "Adicionar contato" },
                { id: "sequences", icon: Sparkles, en: "Sequence Library", pt: "Biblioteca de Sequências" },
              ].map((it) => {
                const Icon = it.icon;
                const isActive = active === it.id;
                return (
                  <button key={it.id} type="button" onClick={() => setActive(it.id)}
                    data-testid={`kanban-tools-tab-${it.id}`}
                    className={`px-4 h-10 inline-flex items-center gap-1.5 text-[12.5px] font-bold whitespace-nowrap ${isActive ? "text-[#3B82F6] border-b-2 border-[#3B82F6]" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
                    <Icon className="w-3.5 h-3.5" /> {isPt ? it.pt : it.en}
                  </button>
                );
              })}
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {active === "import" && <ImportPanel isPt={isPt} onDone={onChanged} />}
              {active === "export" && <ExportPanel isPt={isPt} />}
              {active === "blast" && <BlastPanel isPt={isPt} />}
              {active === "restore" && <RestorePanel isPt={isPt} onDone={onChanged} />}
              {active === "documents" && <DocumentsPanel isPt={isPt} />}
              {active === "add" && <AddContactPanel isPt={isPt} onDone={() => { onChanged?.(); setOpen(false); }} />}
              {active === "sequences" && <SequenceLibraryPanel isPt={isPt} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ImportPanel({ isPt, onDone }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/leads/import`, fd);
      setReport(data);
      toast.success(isPt ? `${data.created || 0} leads importados` : `${data.created || 0} leads imported`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="kanban-tools-import">
      <p className="text-[12.5px] text-[var(--al-text-muted)]">
        {isPt
          ? "Envie um CSV ou XLSX (colunas: name, sector, address, phone, email, website) — máx 5MB."
          : "Upload a CSV or XLSX (columns: name, sector, address, phone, email, website) — max 5MB."}
      </p>
      <label className="block">
        <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} disabled={busy}
          data-testid="kanban-tools-import-file"
          className="block w-full text-[12.5px] file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[#3B82F6] file:text-white file:font-bold file:cursor-pointer hover:file:bg-[#2563eb]" />
      </label>
      {busy && <div className="text-[12px] text-[var(--al-text-muted)] inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {isPt ? "Processando…" : "Processing…"}</div>}
      {report && (
        <div className="text-[12.5px] mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800">
          <b>{report.created || 0}</b> {isPt ? "criados" : "created"} · <b>{report.skipped || 0}</b> {isPt ? "ignorados" : "skipped"} · <b>{report.errors || 0}</b> {isPt ? "erros" : "errors"}
        </div>
      )}
      <div className="text-[11.5px] text-[var(--al-text-muted)] pt-2 border-t border-[var(--al-border)]">
        {isPt ? "Modelo:" : "Template:"} <code>name,sector,address,phone,email,website</code>
      </div>
    </div>
  );
}

function ExportPanel({ isPt }) {
  const [busy, setBusy] = useState("");
  const { onTrial } = useTrialLock();

  const exportCsv = async () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar leads" : "export leads")) return;
    setBusy("csv");
    try {
      const { data } = await axios.get(`${API}/saved-leads`);
      const items = data.leads || [];
      if (items.length === 0) { toast.info(isPt ? "Sem leads salvos." : "No saved leads."); return; }
      const header = ["id","name","sector","address","phone","email","website","crm_status","badge","digital_score"];
      const rows = items.map((p) => header.map((k) => `"${String(p[k] ?? "").replace(/"/g, '""')}"`).join(","));
      const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `leads_${Date.now()}.csv`;
      a.click();
      toast.success(isPt ? `${items.length} leads exportados (CSV)` : `${items.length} leads exported (CSV)`);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(""); }
  };

  const exportXlsx = async () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar leads" : "export leads")) return;
    setBusy("xlsx");
    try {
      const res = await axios.get(`${API}/leads/export-xlsx`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `agentelead_leads_${Date.now()}.xlsx`;
      a.click();
      toast.success(isPt ? "Planilha XLSX baixada" : "XLSX downloaded");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(""); }
  };

  const exportSheets = async () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar leads" : "export leads")) return;
    setBusy("sheets");
    try {
      const { data } = await axios.post(`${API}/leads/export-google-sheets`);
      toast.success(isPt ? `${data.exported || 0}/${data.total || 0} leads → Google Sheets` : `${data.exported || 0}/${data.total || 0} leads pushed to Google Sheets`);
      if (data.sheet_url) window.open(data.sheet_url, "_blank");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="kanban-tools-export">
      <p className="text-[12.5px] text-[var(--al-text-muted)]">
        {isPt ? "Baixe todos os leads salvos no formato que preferir." : "Download every saved lead in the format you prefer."}
      </p>
      <div className="grid sm:grid-cols-3 gap-2">
        <button type="button" onClick={exportCsv} disabled={!!busy}
          data-testid="kanban-tools-export-csv"
          className="h-11 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563eb] text-white font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          CSV
        </button>
        <button type="button" onClick={exportXlsx} disabled={!!busy}
          data-testid="kanban-tools-export-xlsx"
          className="h-11 px-4 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          Excel (.xlsx)
        </button>
        <button type="button" onClick={exportSheets} disabled={!!busy}
          data-testid="kanban-tools-export-sheets"
          className="h-11 px-4 rounded-lg bg-[#0F9D58] hover:bg-[#0b7c45] text-white font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy === "sheets" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Google Sheets
        </button>
      </div>
      <p className="text-[11px] text-[var(--al-text-muted)] pt-2 border-t border-[var(--al-border)]">
        {isPt
          ? "O Google Sheets usa a planilha já vinculada à conta. O link abre automaticamente após a sincronização."
          : "Google Sheets pushes to the spreadsheet linked to this account. The link opens once the sync completes."}
      </p>
    </div>
  );
}

function BlastPanel({ isPt }) {
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState(
    isPt
      ? "{{firstName}}, uma ideia rápida para a {{company}}"
      : "{{firstName}}, a quick idea for {{company}}",
  );
  const [body, setBody] = useState(
    isPt
      ? `Oi {{firstName}},\n\nVi a {{company}} em {{city}} e tenho uma ideia que pode trazer 15-25 leads qualificados/mês.\n\nAbre 10 min amanhã às 14h?\n\n— Carlos`
      : `Hi {{firstName}},\n\nI noticed {{company}} in {{city}} and have an idea that could bring you 15-25 qualified leads/month.\n\nOpen for 10 min tomorrow at 2pm?\n\n— Carlos`,
  );
  const [crmStatus, setCrmStatus] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [report, setReport] = useState(null);
  const { onTrial } = useTrialLock();

  const send = async () => {
    if (blockExportIfTrial(onTrial, isPt ? "disparar e-mails em massa" : "send blast emails")) return;
    if (!subject.trim() || !body.trim()) {
      toast.error(isPt ? "Preencha assunto e corpo." : "Subject and body are required.");
      return;
    }
    if (!window.confirm(isPt
      ? `Enviar e-mail para todos os leads salvos${crmStatus ? ` no estágio ${crmStatus}` : ""}?`
      : `Send to every saved lead${crmStatus ? ` in ${crmStatus}` : ""}?`)) return;
    setBusy(true); setReport(null);
    try {
      const { data } = await axios.post(`${API}/email/resend/blast`, {
        recipients: [],
        subject, body,
        use_saved_leads: true,
        crm_status: crmStatus || null,
        reply_to: replyTo || null,
      });
      setReport(data);
      toast.success(isPt
        ? `${data.sent}/${data.total} enviados via Resend`
        : `${data.sent}/${data.total} sent via Resend`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="kanban-tools-blast">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-[11.5px] text-blue-900">
        <Send className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="leading-snug">
          {isPt
            ? "Disparo via Resend. Personalize com {{firstName}}, {{company}}, {{city}}. Limite: 500 destinatários por envio."
            : "Powered by Resend. Personalise with {{firstName}}, {{company}}, {{city}}. Capped at 500 recipients per blast."}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Estágio do CRM (opcional)" : "CRM stage (optional)"}
          </div>
          <select value={crmStatus} onChange={(e) => setCrmStatus(e.target.value)}
            data-testid="kanban-tools-blast-status"
            className="al-input h-9 text-[12.5px] w-full">
            <option value="">{isPt ? "Todos os leads salvos" : "All saved leads"}</option>
            {["NEW","CONTACTED","QUALIFIED","PROPOSAL"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">Reply-to</div>
          <input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)}
            placeholder="you@yourdomain.com"
            data-testid="kanban-tools-blast-replyto"
            className="al-input h-9 text-[12.5px] w-full" />
        </label>
      </div>
      <label className="block">
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "Assunto" : "Subject"}
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          data-testid="kanban-tools-blast-subject"
          className="al-input h-10 text-[13px] w-full" />
      </label>
      <label className="block">
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "Corpo" : "Body"}
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
          data-testid="kanban-tools-blast-body"
          className="al-input text-[13px] w-full p-3 leading-relaxed font-mono-tech" />
      </label>
      <div className="flex items-center justify-end pt-2 border-t border-[var(--al-border)]">
        <button type="button" onClick={send} disabled={busy}
          data-testid="kanban-tools-blast-send"
          className="h-11 px-5 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {isPt ? "Disparar agora" : "Send blast"}
        </button>
      </div>
      {report && (
        <div className="text-[12.5px] mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="kanban-tools-blast-report">
          <b>{report.sent}</b> {isPt ? "enviados" : "sent"} · <b>{report.failed}</b> {isPt ? "falhas" : "failed"} · <b>{report.total}</b> {isPt ? "destinatários" : "recipients"}
          {report.errors?.length > 0 && (
            <details className="mt-2 text-[11.5px]">
              <summary className="cursor-pointer">{isPt ? "Ver erros" : "See errors"}</summary>
              <ul className="mt-1 ml-4 list-disc">
                {report.errors.map((e, i) => <li key={i}>{e.email}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function RestorePanel({ isPt, onDone }) {
  const [busy, setBusy] = useState(false);
  const onRestore = async () => {
    if (!window.confirm(isPt ? "Restaurar leads do último snapshot?" : "Restore leads from latest snapshot?")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/leads/restore`);
      toast.success(isPt ? `${data.restored || 0} lead(s) restaurados` : `${data.restored || 0} lead(s) restored`);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="kanban-tools-restore">
      <p className="text-[12.5px] text-[var(--al-text-muted)]">
        {isPt ? "Restaura leads a partir do snapshot diário automático mais recente." : "Restores leads from the most recent automatic daily snapshot."}
      </p>
      <button type="button" onClick={onRestore} disabled={busy}
        data-testid="kanban-tools-restore-btn"
        className="h-10 px-5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        {isPt ? "Restaurar agora" : "Restore now"}
      </button>
    </div>
  );
}

function DocumentsPanel({ isPt }) {
  return (
    <div className="space-y-3" data-testid="kanban-tools-documents">
      <p className="text-[12.5px] text-[var(--al-text-muted)]">
        {isPt
          ? "Anexe contratos, propostas e fichas técnicas a um lead pelo Lead Detail (clique em um card no Kanban → aba Documentos). Geração de catálogo white-label e versionamento automático em breve."
          : "Attach contracts, proposals and spec sheets to a lead from the Lead Detail (click a card in the board → Documents tab). White-label catalog generation and automatic versioning coming soon."}
      </p>
      <ul className="text-[12.5px] space-y-1.5 ml-4 list-disc text-[var(--al-text)]">
        <li>{isPt ? "PDF / DOCX / XLSX (≤ 25MB)" : "PDF / DOCX / XLSX (≤ 25MB)"}</li>
        <li>{isPt ? "Pré-visualização inline" : "Inline preview"}</li>
        <li>{isPt ? "Versionamento por mudança" : "Versioning per change"}</li>
      </ul>
    </div>
  );
}

function AddContactPanel({ isPt, onDone }) {  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", sector: "", address: "", phone: "", email: "", website: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(isPt ? "Nome é obrigatório." : "Name is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/manual-lead`, form);
      toast.success(isPt ? "Lead criado" : "Lead created");
      setForm({ name: "", sector: "", address: "", phone: "", email: "", website: "" });
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const F = (label, key, icon, type = "text", required = false) => {
    const Icon = icon;
    return (
      <label className="block">
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5 inline-flex items-center gap-1">
          {Icon && <Icon className="w-3 h-3" />} {label} {required && <span className="text-rose-500">*</span>}
        </div>
        <input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          data-testid={`kanban-tools-add-${key}`}
          className="w-full al-input h-9 text-[12.5px]" required={required} />
      </label>
    );
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="kanban-tools-add">
      {F(isPt ? "Nome do negócio" : "Business name", "name", Building2, "text", true)}
      {F(isPt ? "Setor / Tipo" : "Sector / Type", "sector", Building2)}
      {F(isPt ? "Endereço" : "Address", "address", Building2)}
      {F(isPt ? "Telefone" : "Phone", "phone", Phone)}
      {F("E-mail", "email", Mail, "email")}
      {F("Website", "website", Globe, "url")}
      <div className="sm:col-span-2 pt-2 border-t border-[var(--al-border)] flex justify-end">
        <button type="submit" disabled={busy}
          data-testid="kanban-tools-add-submit"
          className="h-10 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          {isPt ? "Adicionar lead" : "Add lead"}
        </button>
      </div>
    </form>
  );
}


// Sequence Library — prebuilt cold-email + WhatsApp templates by niche.
// Pure client-side today: users copy → adapt inside Email Modal / WhatsApp widget.
// Later we'll persist user edits to /api/sequences.
const SEQUENCE_TEMPLATES = [
  {
    id: "roofing_v1", niche: "Roofing", lang: "en",
    subject: "Quick question about {{company}}'s roof jobs",
    email: `Hi {{firstName}},

Saw {{company}} on a few active permits in {{city}}. Most roofers in your zip are bleeding 20-30% of incoming calls because the site loads slow on mobile.

Mind if I send a 90s audit of {{website}} for free? If it's useful we talk, if not you kept a PDF 🙂

— Carlos`,
    whatsapp: "Oi {{firstName}}, vi que a {{company}} está ativa em {{city}}. Fiz um mini-audit do site de vocês (grátis, 90s). Te mando aqui?",
    follow_up_days: [0, 3, 7],
  },
  {
    id: "hvac_v1", niche: "HVAC", lang: "en",
    subject: "Cooler leads for {{company}} this month",
    email: `Hey {{firstName}},

AC season is starting. We helped 3 HVAC shops in {{city}} jump from 40 to 120 calls/mo in 6 weeks using Google Maps + AI audits.

Want the teardown we did for {{competitor}} as inspiration? Zero strings.

— Carlos`,
    whatsapp: "Oi {{firstName}}! Temos aberto playbook para HVAC em {{city}}. Posso te mandar o PDF usado com a {{competitor}}?",
    follow_up_days: [0, 2, 5],
  },
  {
    id: "solar_v1", niche: "Solar", lang: "en",
    subject: "{{company}} + 18 qualified solar leads this week",
    email: `Hi {{firstName}},

We've been pulling 15-25 qualified solar homeowners in {{city}} every week for installers like yours — without spend on ads.

Takes 10min to show you. Open tomorrow at 2pm?

— Carlos`,
    whatsapp: "Oi {{firstName}}, 10 min amanhã às 14h? Te mostro como estamos entregando 15-25 leads solar qualificados/semana.",
    follow_up_days: [0, 3, 8],
  },
  {
    id: "gc_v1", niche: "General Contractor", lang: "en",
    subject: "{{bidName}} bid — are you pricing it?",
    email: `Hi {{firstName}},

Noticed {{company}} wasn't on the {{bidName}} ({{value}}) bid list yet. It matches your last 3 projects.

We surface 40+ matching public bids per month. Worth a peek?

— Carlos`,
    whatsapp: "Oi {{firstName}}, viu o {{bidName}} ({{value}})? Entrega por {{city}}. Tá no seu perfil.",
    follow_up_days: [0, 2, 5],
  },
];

function SequenceLibraryPanel({ isPt }) {
  const [filter, setFilter] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success(isPt ? "Copiado" : "Copied");
      setTimeout(() => setCopiedId(""), 1500);
    } catch (_e) { toast.error(isPt ? "Falha ao copiar" : "Failed to copy"); }
  };
  const list = SEQUENCE_TEMPLATES.filter((s) =>
    !filter || s.niche.toLowerCase().includes(filter.toLowerCase()) ||
    s.subject.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="kanban-tools-sequences">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200">
        <Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
        <div className="text-[11.5px] text-violet-900 leading-snug">
          {isPt
            ? "Templates pré-testados por nicho. Copie, troque {{placeholders}} e cole no Email Modal ou WhatsApp Widget. Métricas médias: 32% open, 11% reply."
            : "Prebuilt templates by niche. Copy, swap {{placeholders}} and paste into Email Modal / WhatsApp Widget. Average metrics: 32% open, 11% reply."}
        </div>
      </div>
      <input
        value={filter} onChange={(e) => setFilter(e.target.value)}
        data-testid="kanban-tools-seq-filter"
        placeholder={isPt ? "Filtrar por nicho (roofing, hvac, solar…)" : "Filter by niche (roofing, hvac, solar…)"}
        className="w-full al-input h-9 text-[12.5px]" />
      <div className="space-y-3">
        {list.length === 0 && <div className="text-center text-[12px] text-[var(--al-text-muted)] py-6">{isPt ? "Nenhum template." : "No templates."}</div>}
        {list.map((seq) => (
          <div key={seq.id} className="al-card p-4" data-testid={`kanban-tools-seq-${seq.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#0A1233] text-[#C9A24E]">{seq.niche}</span>
              <span className="text-[10px] text-[var(--al-text-muted)]">
                {isPt ? "Follow-up:" : "Follow-up:"} {seq.follow_up_days.join(" → ")} {isPt ? "dias" : "days"}
              </span>
            </div>
            <div className="text-[13px] font-bold mb-2">📧 {seq.subject}</div>
            <pre className="text-[12px] whitespace-pre-wrap font-mono-tech bg-[var(--al-bg)] p-3 rounded border border-[var(--al-border)] max-h-40 overflow-y-auto">{seq.email}</pre>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={() => copy(seq.email, `${seq.id}-email`)}
                data-testid={`kanban-tools-seq-copy-email-${seq.id}`}
                className="h-8 px-3 rounded-md bg-[#3B82F6] hover:bg-[#2563eb] text-white text-[11px] font-bold inline-flex items-center gap-1">
                {copiedId === `${seq.id}-email` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {isPt ? "Copiar e-mail" : "Copy email"}
              </button>
              <button type="button" onClick={() => copy(seq.whatsapp, `${seq.id}-wa`)}
                data-testid={`kanban-tools-seq-copy-wa-${seq.id}`}
                className="h-8 px-3 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white text-[11px] font-bold inline-flex items-center gap-1">
                {copiedId === `${seq.id}-wa` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                WhatsApp
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
